import React, { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import { Plus, Trash2, Edit, Save, X, ChevronDown, ChevronUp, GripVertical, Eye, EyeOff } from 'lucide-react';
import ImageUploadField from './ImageUploadField';
import FocalPointPicker from './FocalPointPicker';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BLOCK_TYPES = [
  { id: 'text', label: 'Text Block', icon: '📝' },
  { id: 'image', label: 'Image', icon: '🖼️' },
  { id: 'gallery', label: 'Gallery', icon: '🎨' },
  { id: 'video', label: 'Video', icon: '🎬' },
  { id: 'quote', label: 'Quote', icon: '💬' },
  { id: 'stats', label: 'Stats', icon: '📊' },
  { id: 'faq', label: 'FAQ Section', icon: '❓' },
  { id: 'cta', label: 'Call to Action', icon: '🎯' }
];

const ServicesPanel = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingService, setEditingService] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const token = api.getToken();
      const res = await fetch(`${API_URL}/api/admin/services`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setServices(data || []);
    } catch (error) {
      console.error('Error fetching services:', error);
      setMessage({ type: 'error', text: 'Failed to load services' });
    } finally {
      setLoading(false);
    }
  };

  const seedDefaultServices = async () => {
    try {
      const token = api.getToken();
      const res = await fetch(`${API_URL}/api/admin/services/seed-defaults`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setMessage({ type: 'success', text: data.message });
        fetchServices();
      } else {
        setMessage({ type: 'error', text: data.message });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to seed services' });
    }
  };

  const handleCreate = () => {
    setEditingService({
      slug: '',
      title: '',
      short_description: '',
      icon: 'camera',
      cover_image_url: '',
      content_blocks: [],
      is_active: true,
      order: services.length,
      seo_title: '',
      seo_description: ''
    });
    setShowEditor(true);
  };

  const handleEdit = (service) => {
    setEditingService({ ...service });
    setShowEditor(true);
  };

  const handleSave = async () => {
    if (!editingService.title || !editingService.slug) {
      setMessage({ type: 'error', text: 'Title and slug are required' });
      return;
    }

    setSaving(true);
    try {
      const token = api.getToken();
      const isNew = !editingService.id;
      const url = isNew 
        ? `${API_URL}/api/admin/services`
        : `${API_URL}/api/admin/services/${editingService.id}`;
      
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editingService)
      });

      if (res.ok) {
        setMessage({ type: 'success', text: `Service ${isNew ? 'created' : 'updated'} successfully` });
        setShowEditor(false);
        setEditingService(null);
        fetchServices();
      } else {
        const error = await res.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to save service' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save service' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;

    try {
      const token = api.getToken();
      const res = await fetch(`${API_URL}/api/admin/services/${serviceId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        setMessage({ type: 'success', text: 'Service deleted' });
        fetchServices();
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete service' });
    }
  };

  const handleToggleActive = async (service) => {
    try {
      const token = api.getToken();
      await fetch(`${API_URL}/api/admin/services/${service.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ...service, is_active: !service.is_active })
      });
      fetchServices();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update service' });
    }
  };

  // Block management
  const addBlock = (type) => {
    const newBlock = {
      id: `block-${Date.now()}`,
      type,
      order: editingService.content_blocks.length,
      heading: '',
      subheading: '',
      content: '',
      image_url: '',
      image_caption: '',
      video_url: '',
      gallery_images: [],
      quote_text: '',
      quote_author: '',
      stats: [],
      faqs: [],
      cta_text: 'Learn More',
      cta_link: '/inquire',
      layout: 'full',
      background: 'white'
    };
    setEditingService({
      ...editingService,
      content_blocks: [...editingService.content_blocks, newBlock]
    });
  };

  const updateBlock = (blockId, updates) => {
    setEditingService({
      ...editingService,
      content_blocks: editingService.content_blocks.map(b =>
        b.id === blockId ? { ...b, ...updates } : b
      )
    });
  };

  const removeBlock = (blockId) => {
    setEditingService({
      ...editingService,
      content_blocks: editingService.content_blocks.filter(b => b.id !== blockId)
    });
  };

  const moveBlock = (blockId, direction) => {
    const blocks = [...editingService.content_blocks];
    const index = blocks.findIndex(b => b.id === blockId);
    if (direction === 'up' && index > 0) {
      [blocks[index], blocks[index - 1]] = [blocks[index - 1], blocks[index]];
    } else if (direction === 'down' && index < blocks.length - 1) {
      [blocks[index], blocks[index + 1]] = [blocks[index + 1], blocks[index]];
    }
    blocks.forEach((b, i) => b.order = i);
    setEditingService({ ...editingService, content_blocks: blocks });
  };

  const renderBlockEditor = (block, index) => {
    return (
      <div key={block.id} className="border rounded-lg p-4 mb-4 bg-gray-50">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <GripVertical size={16} className="text-gray-400" />
            <span className="font-medium capitalize">{block.type} Block</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => moveBlock(block.id, 'up')} className="p-1 hover:bg-gray-200 rounded" disabled={index === 0}>
              <ChevronUp size={16} />
            </button>
            <button onClick={() => moveBlock(block.id, 'down')} className="p-1 hover:bg-gray-200 rounded" disabled={index === editingService.content_blocks.length - 1}>
              <ChevronDown size={16} />
            </button>
            <button onClick={() => removeBlock(block.id)} className="p-1 hover:bg-red-100 text-red-500 rounded">
              <Trash2 size={16} />
            </button>
          </div>
        </div>

        {/* Block-specific fields */}
        {block.type === 'text' && (
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Subheading (optional)"
              value={block.subheading || ''}
              onChange={(e) => updateBlock(block.id, { subheading: e.target.value })}
              className="w-full px-3 py-2 border rounded text-sm"
            />
            <input
              type="text"
              placeholder="Heading"
              value={block.heading || ''}
              onChange={(e) => updateBlock(block.id, { heading: e.target.value })}
              className="w-full px-3 py-2 border rounded"
            />
            <textarea
              placeholder="Content"
              value={block.content || ''}
              onChange={(e) => updateBlock(block.id, { content: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 border rounded"
            />
          </div>
        )}

        {block.type === 'image' && (
          <div className="space-y-3">
            <ImageUploadField
              value={block.image_url || ''}
              onChange={(url) => updateBlock(block.id, { image_url: url })}
              folder="services"
              label=""
            />
            <input
              type="text"
              placeholder="Caption (optional)"
              value={block.image_caption || ''}
              onChange={(e) => updateBlock(block.id, { image_caption: e.target.value })}
              className="w-full px-3 py-2 border rounded text-sm"
            />
            {block.image_url && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <FocalPointPicker
                  imageUrl={block.image_url}
                  focalPoint={block.image_focal_desktop || { x: 50, y: 50 }}
                  onChange={(fp) => updateBlock(block.id, { image_focal_desktop: fp })}
                  label="Desktop Focus"
                />
                <FocalPointPicker
                  imageUrl={block.image_url}
                  focalPoint={block.image_focal_mobile || { x: 50, y: 50 }}
                  onChange={(fp) => updateBlock(block.id, { image_focal_mobile: fp })}
                  label="Mobile Focus"
                />
              </div>
            )}
          </div>
        )}

        {block.type === 'video' && (
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Video URL"
              value={block.video_url || ''}
              onChange={(e) => updateBlock(block.id, { video_url: e.target.value })}
              className="w-full px-3 py-2 border rounded"
            />
            <ImageUploadField
              value={block.image_url || ''}
              onChange={(url) => updateBlock(block.id, { image_url: url })}
              folder="services"
              label="Poster Image"
            />
          </div>
        )}

        {block.type === 'quote' && (
          <div className="space-y-3">
            <textarea
              placeholder="Quote text"
              value={block.quote_text || ''}
              onChange={(e) => updateBlock(block.id, { quote_text: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border rounded"
            />
            <input
              type="text"
              placeholder="Author (optional)"
              value={block.quote_author || ''}
              onChange={(e) => updateBlock(block.id, { quote_author: e.target.value })}
              className="w-full px-3 py-2 border rounded text-sm"
            />
          </div>
        )}

        {block.type === 'cta' && (
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Heading"
              value={block.heading || ''}
              onChange={(e) => updateBlock(block.id, { heading: e.target.value })}
              className="w-full px-3 py-2 border rounded"
            />
            <textarea
              placeholder="Description"
              value={block.content || ''}
              onChange={(e) => updateBlock(block.id, { content: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border rounded"
            />
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                placeholder="Button text"
                value={block.cta_text || ''}
                onChange={(e) => updateBlock(block.id, { cta_text: e.target.value })}
                className="px-3 py-2 border rounded text-sm"
              />
              <input
                type="text"
                placeholder="Button link"
                value={block.cta_link || ''}
                onChange={(e) => updateBlock(block.id, { cta_link: e.target.value })}
                className="px-3 py-2 border rounded text-sm"
              />
            </div>
          </div>
        )}

        {/* Layout options */}
        <div className="mt-4 pt-4 border-t flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500">Layout:</label>
            <select
              value={block.layout || 'full'}
              onChange={(e) => updateBlock(block.id, { layout: e.target.value })}
              className="px-2 py-1 border rounded text-sm"
            >
              <option value="full">Full Width</option>
              <option value="centered">Centered</option>
              <option value="left">Left Aligned</option>
              <option value="right">Right Aligned</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500">Background:</label>
            <select
              value={block.background || 'white'}
              onChange={(e) => updateBlock(block.id, { background: e.target.value })}
              className="px-2 py-1 border rounded text-sm"
            >
              <option value="white">White</option>
              <option value="light">Light Gray</option>
              <option value="accent">Dark/Accent</option>
            </select>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  // Service Editor Modal
  if (showEditor && editingService) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">
            {editingService.id ? 'Edit Service' : 'New Service'}
          </h2>
          <button onClick={() => { setShowEditor(false); setEditingService(null); }} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>

        {message.text && (
          <div className={`p-3 rounded ${message.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
            {message.text}
          </div>
        )}

        {/* Basic Info */}
        <div className="border rounded-lg p-4 space-y-4">
          <h3 className="font-medium">Basic Information</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Title *</label>
              <input
                type="text"
                value={editingService.title}
                onChange={(e) => setEditingService({ ...editingService, title: e.target.value })}
                className="w-full px-3 py-2 border rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">URL Slug *</label>
              <input
                type="text"
                value={editingService.slug}
                onChange={(e) => setEditingService({ ...editingService, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                className="w-full px-3 py-2 border rounded"
                placeholder="wedding-photography"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Short Description</label>
            <textarea
              value={editingService.short_description}
              onChange={(e) => setEditingService({ ...editingService, short_description: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border rounded"
              placeholder="Brief description for the services listing page"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Icon</label>
              <select
                value={editingService.icon}
                onChange={(e) => setEditingService({ ...editingService, icon: e.target.value })}
                className="w-full px-3 py-2 border rounded"
              >
                <option value="camera">📷 Camera</option>
                <option value="video">🎬 Video</option>
                <option value="heart">❤️ Heart</option>
                <option value="clock">⏰ Clock</option>
                <option value="plane">✈️ Plane/Drone</option>
                <option value="globe">🌍 Globe</option>
                <option value="sparkles">✨ Sparkles</option>
                <option value="smile">😊 Smile</option>
              </select>
            </div>
            <div>
              <ImageUploadField
                value={editingService.cover_image_url}
                onChange={(url) => setEditingService({ ...editingService, cover_image_url: url })}
                folder="services"
                label="Cover Image"
              />
            </div>
          </div>
        </div>

        {/* Content Blocks */}
        <div className="border rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium">Content Blocks</h3>
            <div className="flex gap-2 flex-wrap">
              {BLOCK_TYPES.map(type => (
                <button
                  key={type.id}
                  onClick={() => addBlock(type.id)}
                  className="px-3 py-1 text-xs border rounded hover:bg-gray-100"
                >
                  {type.icon} {type.label}
                </button>
              ))}
            </div>
          </div>

          {editingService.content_blocks.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No content blocks yet. Add blocks above to build your service page.</p>
          ) : (
            editingService.content_blocks
              .sort((a, b) => a.order - b.order)
              .map((block, index) => renderBlockEditor(block, index))
          )}
        </div>

        {/* SEO */}
        <div className="border rounded-lg p-4 space-y-4">
          <h3 className="font-medium">SEO Settings (Optional)</h3>
          <input
            type="text"
            placeholder="SEO Title"
            value={editingService.seo_title || ''}
            onChange={(e) => setEditingService({ ...editingService, seo_title: e.target.value })}
            className="w-full px-3 py-2 border rounded"
          />
          <textarea
            placeholder="SEO Description"
            value={editingService.seo_description || ''}
            onChange={(e) => setEditingService({ ...editingService, seo_description: e.target.value })}
            rows={2}
            className="w-full px-3 py-2 border rounded"
          />
        </div>

        {/* Save Button */}
        <div className="flex justify-end gap-3">
          <button
            onClick={() => { setShowEditor(false); setEditingService(null); }}
            className="px-6 py-2 border rounded hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-black text-white rounded hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2"
          >
            <Save size={16} />
            {saving ? 'Saving...' : 'Save Service'}
          </button>
        </div>
      </div>
    );
  }

  // Services List
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Services</h2>
          <p className="text-sm text-gray-500 mt-1">Manage your service offerings</p>
        </div>
        <div className="flex gap-2">
          {services.length === 0 && (
            <button
              onClick={seedDefaultServices}
              className="px-4 py-2 border rounded hover:bg-gray-50 text-sm"
            >
              Create Default Services
            </button>
          )}
          <button
            onClick={handleCreate}
            className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 flex items-center gap-2"
          >
            <Plus size={16} />
            Add Service
          </button>
        </div>
      </div>

      {message.text && (
        <div className={`p-3 rounded ${message.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
          {message.text}
        </div>
      )}

      {services.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-lg">
          <p className="text-gray-400 mb-4">No services yet</p>
          <button onClick={seedDefaultServices} className="text-blue-600 hover:underline">
            Click here to create default services
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {services.map((service) => (
            <div key={service.id} className="border rounded-lg p-4 flex items-center justify-between hover:bg-gray-50">
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${service.is_active ? 'bg-black text-white' : 'bg-gray-200 text-gray-400'}`}>
                  {service.icon === 'camera' && '📷'}
                  {service.icon === 'video' && '🎬'}
                  {service.icon === 'heart' && '❤️'}
                  {service.icon === 'clock' && '⏰'}
                  {service.icon === 'plane' && '✈️'}
                  {service.icon === 'globe' && '🌍'}
                  {service.icon === 'sparkles' && '✨'}
                  {service.icon === 'smile' && '😊'}
                </div>
                <div>
                  <h3 className={`font-medium ${!service.is_active && 'text-gray-400'}`}>{service.title}</h3>
                  <p className="text-sm text-gray-400">/services/{service.slug}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleToggleActive(service)}
                  className={`p-2 rounded ${service.is_active ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'}`}
                  title={service.is_active ? 'Active - Click to hide' : 'Hidden - Click to show'}
                >
                  {service.is_active ? <Eye size={18} /> : <EyeOff size={18} />}
                </button>
                <button
                  onClick={() => handleEdit(service)}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                >
                  <Edit size={18} />
                </button>
                <button
                  onClick={() => handleDelete(service.id)}
                  className="p-2 text-red-500 hover:bg-red-50 rounded"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ServicesPanel;
